import { useContext } from 'react';
import type { Experiment } from '@gonfalon/experiments/types';
import {
  assignmentSourceCheckFailedMessage,
  type AssignmentSourceProbeResult,
  getAssignmentSourceProbeParams,
  useAssignmentSourceProbe,
} from '@gonfalon/experiments/useAssignmentSourceProbe';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { useProjectKey } from '@gonfalon/router/useProjectKey';
import {
  Alert,
  Button,
  Heading,
  IconButton,
  Perceivable,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';

import { DraftExperimentFormContext } from '#components/experimentation/common/providers/DraftExperimentFormProvider';

import { TurnOnFlagAlert } from './TurnOnFlagAlert';

import styles from './StartExperimentModal.module.css';

type StartExperimentModalProps = {
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
  isMAB: boolean;
  isHoldout?: boolean;
  isFlagOff?: boolean;
  experiment?: Experiment;
  flagKey?: string;
  hasStratifiedSampling?: boolean;
  assignsFromWarehouse?: boolean;
};

export function StartExperimentModal({
  onCancel,
  onConfirm,
  isPending,
  isMAB,
  isHoldout = false,
  isFlagOff = false,
  experiment,
  flagKey,
  hasStratifiedSampling = false,
  assignsFromWarehouse = false,
}: StartExperimentModalProps) {
  const typeString = isMAB ? 'multi-armed bandit' : isHoldout ? 'holdout' : 'experiment';
  const projectKey = useProjectKey();
  const formContext = useContext(DraftExperimentFormContext);
  const probeParams = getAssignmentSourceProbeParams(
    formContext?.form.state.values.iterationProps?.assignmentSource,
    experiment?.draftIteration?.assignmentDataSource ?? experiment?.currentIteration?.assignmentDataSource,
  );
  const assignmentSourceProbe = useAssignmentSourceProbe({
    projectKey,
    ...probeParams,
    enabled: assignsFromWarehouse,
  });
  const isProbePending = assignmentSourceProbe.status === 'pending';
  const isStartDisabled = isPending || isFlagOff || assignmentSourceProbe.status === 'blocked';

  return (
    <>
      <div slot="header">
        {!isHoldout && (
          <Heading slot="title" aria-label={msg('experiments.startExperimentDialog.heading', { type: typeString })}>
            <Message id="experiments.startExperimentDialog.heading" values={{ type: typeString }} />
          </Heading>
        )}
        {isHoldout && (
          <Heading aria-label={msg('experiments.startExperimentDialog.holdoutHeadingLabel')}>
            <Message id="experiments.startExperimentDialog.holdoutHeading" />
          </Heading>
        )}
        <IconButton
          aria-label={msg('experiments.startExperimentDialog.closeLabel')}
          icon="cancel"
          variant="minimal"
          onPress={onCancel}
        />
      </div>
      <div slot="body">
        {!isHoldout && assignsFromWarehouse && (
          <Text slot="subtitle">
            <Message id="experiments.startExperimentDialog.warehouseSubtitle" values={{ type: typeString }} />
          </Text>
        )}
        {!isHoldout && !assignsFromWarehouse && (
          <Text slot="subtitle">
            <Message id="experiments.startExperimentDialog.subtitle" values={{ type: typeString }} />
          </Text>
        )}
        {isHoldout && (
          <Text slot="subtitle">
            <Message id="experiments.startExperimentDialog.holdoutSubtitle" />
          </Text>
        )}
        {flagKey && <TurnOnFlagAlert typeString={typeString} experiment={experiment} flagKey={flagKey} />}
        {hasStratifiedSampling && isPending && (
          <Alert variant="inline" status="info" className={styles.alert}>
            <Message id="experiments.startExperimentDialog.stratifiedSamplingNotice" />
          </Alert>
        )}
        {assignsFromWarehouse && <AssignmentSourceProbeAlert probe={assignmentSourceProbe} />}
      </div>
      <div slot="footer">
        <Button variant="default" onPress={onCancel} isDisabled={isPending}>
          {msg('experiments.startExperimentDialog.cancel')}
        </Button>
        <StartConfirmButton
          typeString={typeString}
          isHoldout={isHoldout}
          isPending={isPending || isProbePending}
          isDisabled={isStartDisabled}
          pendingTooltip={isProbePending ? 'Checking assignment source size.' : undefined}
          onConfirm={onConfirm}
        />
      </div>
    </>
  );
}

function StartConfirmButton({
  typeString,
  isHoldout,
  isPending,
  isDisabled,
  pendingTooltip,
  onConfirm,
}: {
  typeString: string;
  isHoldout: boolean;
  isPending: boolean;
  isDisabled: boolean;
  pendingTooltip?: string;
  onConfirm: () => void;
}) {
  const button = (
    <Button variant="primary" onPress={onConfirm} isPending={isPending} isDisabled={isDisabled}>
      {!isHoldout && `Start ${typeString}`}
      {isHoldout && `Declare ready`}
    </Button>
  );

  if (!pendingTooltip) {
    return button;
  }

  return (
    <Perceivable>
      <TooltipTrigger delay={0}>
        {button}
        <Tooltip>{pendingTooltip}</Tooltip>
      </TooltipTrigger>
    </Perceivable>
  );
}

function AssignmentSourceProbeAlert({ probe }: { probe: AssignmentSourceProbeResult }) {
  if (probe.status === 'warn' && probe.warningMessage) {
    return (
      <Alert variant="inline" status="warning" className={styles.alert}>
        {probe.warningMessage}
      </Alert>
    );
  }

  if (probe.status === 'blocked' && probe.blockMessage) {
    return (
      <Alert variant="inline" status="error" className={styles.alert}>
        {probe.blockMessage}
      </Alert>
    );
  }

  if (probe.status === 'error') {
    return (
      <Alert variant="inline" status="error" className={styles.alert}>
        {!probe.errorMessage || probe.errorMessage === assignmentSourceCheckFailedMessage
          ? `${assignmentSourceCheckFailedMessage} Start anyway, or cancel and try again.`
          : probe.errorMessage}
      </Alert>
    );
  }

  return null;
}
